import { getAccessToken, waitForAccessToken } from "./authToken";
import { getLeadSummaryApiBase } from "./leadSummaryApi";

/**
 * Full URL to POST /leads (list with filters, search, sort, page).
 * Prefer `VITE_LEADS_LIST_URL` for a full override; else `${getLeadSummaryApiBase()}/leads`
 * (see `VITE_LEAD_SERVICES_BASE` / `VITE_LEAD_SUMMARY_API_BASE` in `getLeadSummaryApiBase`).
 */
/** Rows from recent POST /leads responses in this session (avoids per-CIN refetch on detail). */
let leadsListSessionCache = [];

/**
 * Merge list rows into the session cache (keyed by CIN).
 * @param {unknown[]} items
 */
export function mergeLeadsIntoSessionCache(items) {
    if (!Array.isArray(items) || items.length === 0) {
        return;
    }
    const byCin = new Map(
        leadsListSessionCache.map((r) => [String(r?.cin || "").trim().toUpperCase(), r])
    );
    for (const row of items) {
        const key = String(row?.cin || "").trim().toUpperCase();
        if (key) {
            byCin.set(key, row);
        }
    }
    leadsListSessionCache = Array.from(byCin.values());
}

/**
 * Lookup a lead row from the session cache (from prior list/search/pagination calls).
 * @param {string} cin
 * @returns {Record<string, unknown>|null}
 */
export function getCachedLeadRowByCin(cin) {
    return findLeadRowByCin(leadsListSessionCache, cin);
}

export function getLeadsListUrl() {
    const explicit = String(import.meta.env.VITE_LEADS_LIST_URL || "").trim();
    if (explicit) {
        return explicit;
    }
    const base = getLeadSummaryApiBase();
    return base ? `${base}/leads` : "";
}

/**
 * Map the search box to POST /leads `search` (`cin` or `first_name`).
 * After trim: starts with a digit -> `cin` (spaces/dashes stripped, uppercased);
 * starts with a letter -> `first_name` (trimmed value as entered).
 * Only one field is set at a time; the other stays blank.
 * Empty -> both blank.
 * @param {string} raw
 * @returns {{ cin: string, first_name: string }}
 */
export function leadsListQueryFromSearchInput(raw) {
    const empty = { cin: "", first_name: "" };
    const t = String(raw || "").trim();
    if (!t) {
        return empty;
    }
    const first = t.charAt(0);
    if (/^\d$/.test(first)) {
        const cin = t.replace(/[\s-]/g, "").toUpperCase();
        return { cin: cin || "", first_name: "" };
    }
    if (/^[A-Za-z]$/.test(first)) {
        return { cin: "", first_name: t };
    }
    return { cin: "", first_name: t };
}

/**
 * Empty search payload for POST /leads.
 * @returns {{ cin: string, first_name: string }}
 */
export function createEmptyLeadsListSearch() {
    return { cin: "", first_name: "" };
}

/**
 * Build POST /leads `search` object. Only one of cin / first_name is filled.
 * @param {{
 *   search?: { cin?: string, first_name?: string, firstName?: string } | null,
 *   cin?: string,
 *   firstName?: string,
 *   first_name?: string
 * }} [options]
 * @returns {{ cin: string, first_name: string }}
 */
export function buildLeadsListSearchBody(options = {}) {
    if (options.search && typeof options.search === "object") {
        const cin = String(options.search.cin || "").trim();
        const firstName = String(
            options.search.first_name || options.search.firstName || ""
        ).trim();
        if (cin) {
            return { cin, first_name: "" };
        }
        if (firstName) {
            return { cin: "", first_name: firstName };
        }
        return createEmptyLeadsListSearch();
    }
    const cin = String(options.cin || "").trim();
    const firstName = String(options.firstName || options.first_name || "").trim();
    if (cin) {
        return { cin, first_name: "" };
    }
    if (firstName) {
        return { cin: "", first_name: firstName };
    }
    return createEmptyLeadsListSearch();
}

/**
 * Empty filter payload for POST /leads.
 * @returns {{ lead_score: string[], waystar_authorization: string[], lead_sourced_by: string[] }}
 */
export function createEmptyLeadsListFilters() {
    return {
        lead_score: [],
        waystar_authorization: [],
        lead_sourced_by: []
    };
}

/**
 * Map UI filter groups to the POST /leads `filters` object.
 * Eligibility UI values map to `waystar_authorization` (not ECM eligibility).
 * Outreach by UI values map to `lead_sourced_by` (not `lead_source`).
 * @param {{ clinicalScore?: string[], eligibility?: string[], leadSource?: string[] } | null | undefined} uiFilters
 */
export function buildLeadsListFiltersBody(uiFilters) {
    const src = uiFilters || {};
    return {
        lead_score: Array.isArray(src.clinicalScore) ? [...src.clinicalScore] : [],
        waystar_authorization: Array.isArray(src.eligibility) ? [...src.eligibility] : [],
        lead_sourced_by: Array.isArray(src.leadSource) ? [...src.leadSource] : []
    };
}

/** UI sort keys → API `sort.field` names. */
const LEADS_LIST_SORT_FIELD_BY_KEY = {
    enrollment: "ecm_eligibility",
    clinicalScore: "lead_score",
    eligibility: "waystar_authorization",
    leadSource: "lead_source",
    dob: "dob",
    summaryDate: "summary_date"
};

/**
 * Map UI sort state to the POST /leads `sort` object.
 * @param {string | null | undefined} sortKey
 * @param {"asc" | "desc" | null | undefined} sortDirection
 * @returns {{ field: string, order: string }}
 */
export function buildLeadsListSortBody(sortKey, sortDirection) {
    const field = LEADS_LIST_SORT_FIELD_BY_KEY[sortKey] || "";
    if (!field || (sortDirection !== "asc" && sortDirection !== "desc")) {
        return { field: "", order: "" };
    }
    return {
        field,
        order: sortDirection === "desc" ? "DESC" : "ASC"
    };
}

/**
 * Build the JSON body for POST /leads.
 * @param {{
 *   page?: number,
 *   pageSize?: number,
 *   filters?: { clinicalScore?: string[], eligibility?: string[], leadSource?: string[] } | null,
 *   sortKey?: string | null,
 *   sortDirection?: "asc" | "desc" | null,
 *   search?: { cin?: string, first_name?: string, firstName?: string } | null,
 *   firstName?: string,
 *   cin?: string
 * }} [options]
 */
export function buildLeadsListRequestBody(options = {}) {
    const pageNum = Number(options.page);
    const pageSizeNum = Number(options.pageSize ?? options.page_size);
    const pageSize =
        Number.isFinite(pageSizeNum) && pageSizeNum > 0 ? Math.floor(pageSizeNum) : 50;
    return {
        filters: buildLeadsListFiltersBody(options.filters),
        search: buildLeadsListSearchBody(options),
        sort: buildLeadsListSortBody(options.sortKey, options.sortDirection),
        page: Number.isFinite(pageNum) && pageNum >= 1 ? Math.floor(pageNum) : 1,
        page_size: pageSize
    };
}

/**
 * POST /leads with filters, search, sort, page, and page_size (same Bearer auth).
 * Base URL from `options.listUrl`, `VITE_LEADS_LIST_URL`, or `${getLeadSummaryApiBase()}/leads`.
 * @param {{
 *   page?: number,
 *   pageSize?: number,
 *   filters?: { clinicalScore?: string[], eligibility?: string[], leadSource?: string[] } | null,
 *   sortKey?: string | null,
 *   sortDirection?: "asc" | "desc" | null,
 *   search?: { cin?: string, first_name?: string, firstName?: string } | null,
 *   firstName?: string,
 *   cin?: string,
 *   listUrl?: string,
 *   skipSessionCache?: boolean
 * }} [options]
 * @returns {Promise<{
 *   items: unknown[],
 *   count: number,
 *   page: number,
 *   pageSize: number,
 *   totalCount: number,
 *   totalPages: number,
 *   hasNext: boolean,
 *   hasPrev: boolean
 * }>}
 */
export async function fetchLeadsList(options = {}) {
    const baseUrl = String(options.listUrl || getLeadsListUrl() || "").trim();
    if (!baseUrl) {
        throw new Error(
            "Leads list API is not configured (set VITE_LEAD_SERVICES_BASE, VITE_LEAD_SUMMARY_API_BASE, or VITE_LEADS_LIST_URL)."
        );
    }

    await waitForAccessToken(5000);
    const token = getAccessToken();
    if (!token) {
        throw new Error("You must be signed in to view leads.");
    }

    const body = buildLeadsListRequestBody(options);

    const res = await fetch(baseUrl, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
    });

    if (!res.ok) {
        let detail = "";
        try {
            const errBody = await res.json();
            detail = errBody.message || errBody.error || errBody.detail || "";
        } catch {
            try {
                detail = await res.text();
            } catch {
                detail = "";
            }
        }
        const msg = detail ? `${detail}` : `Request failed (${res.status})`;
        const err = new Error(msg);
        err.status = res.status;
        throw err;
    }

    const rawText = await res.text();
    let data;
    try {
        data = rawText ? JSON.parse(rawText) : null;
    } catch {
        throw new Error("Leads API returned invalid JSON.");
    }

    const page = normalizeLeadsPage(data);
    if (page.items.length > 0 && !options.skipSessionCache) {
        mergeLeadsIntoSessionCache(page.items);
    }
    return page;
}

/**
 * Parse list body and page-number pagination metadata.
 * @returns {{
 *   items: unknown[],
 *   count: number,
 *   page: number,
 *   pageSize: number,
 *   totalCount: number,
 *   totalPages: number,
 *   hasNext: boolean,
 *   hasPrev: boolean
 * }}
 */
export function normalizeLeadsPage(data) {
    const items = normalizeLeadsResponse(data);
    if (!data || typeof data !== "object" || Array.isArray(data)) {
        return {
            items,
            count: items.length,
            page: 1,
            pageSize: items.length || 10,
            totalCount: items.length,
            totalPages: items.length > 0 ? 1 : 0,
            hasNext: false,
            hasPrev: false
        };
    }

    const page = toPositiveInt(data.page, 1);
    const pageSize = toPositiveInt(data.page_size ?? data.pageSize, 50);
    const count = toNonNegativeInt(data.count, items.length);
    const totalCount = toNonNegativeInt(data.total_count ?? data.totalCount, items.length);
    let totalPages = toNonNegativeInt(data.total_pages ?? data.totalPages, 0);
    if (totalPages === 0 && totalCount > 0 && pageSize > 0) {
        totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    } else if (totalPages === 0 && items.length > 0) {
        totalPages = 1;
    }

    const hasNext =
        typeof data.has_next === "boolean"
            ? data.has_next
            : typeof data.hasNext === "boolean"
              ? data.hasNext
              : page < totalPages;
    const hasPrev =
        typeof data.has_prev === "boolean"
            ? data.has_prev
            : typeof data.hasPrev === "boolean"
              ? data.hasPrev
              : page > 1;

    return {
        items,
        count,
        page,
        pageSize,
        totalCount,
        totalPages,
        hasNext,
        hasPrev
    };
}

/**
 * @param {unknown} value
 * @param {number} fallback
 */
function toPositiveInt(value, fallback) {
    const n = Number(value);
    if (Number.isFinite(n) && n >= 1) {
        return Math.floor(n);
    }
    return fallback;
}

/**
 * @param {unknown} value
 * @param {number} fallback
 */
function toNonNegativeInt(value, fallback) {
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) {
        return Math.floor(n);
    }
    return fallback;
}

/**
 * API may return a bare array, a JSON string of an array, or wrapped shapes
 * (e.g. API Gateway/Lambda proxy { body: "[...]" }, { data: [...] }).
 */
export function normalizeLeadsResponse(data) {
    if (data == null) {
        return [];
    }
    if (Array.isArray(data)) {
        return data;
    }
    if (typeof data === "string") {
        const t = data.trim();
        if (!t) {
            return [];
        }
        try {
            return normalizeLeadsResponse(JSON.parse(t));
        } catch {
            return [];
        }
    }
    if (typeof data === "object") {
        if (Array.isArray(data.Items)) {
            return data.Items;
        }
        if (Array.isArray(data.items)) {
            return data.items;
        }
        if (Array.isArray(data.leads)) {
            return data.leads;
        }
        if (Array.isArray(data.data)) {
            return data.data;
        }
        if (Array.isArray(data.results)) {
            return data.results;
        }
        if (typeof data.body === "string") {
            try {
                return normalizeLeadsResponse(JSON.parse(data.body));
            } catch {
                return [];
            }
        }
        if (Array.isArray(data.body)) {
            return data.body;
        }
        const keys = Object.keys(data);
        if (
            keys.length > 0 &&
            keys.every((k) => /^(0|[1-9]\d*)$/.test(k))
        ) {
            return keys
                .sort((a, b) => Number(a) - Number(b))
                .map((k) => data[k]);
        }
    }
    return [];
}

/**
 * Match a row from POST /leads by CIN (case-insensitive).
 * @param {unknown[]} leads
 * @param {string} cin
 * @returns {Record<string, unknown>|null}
 */
export function findLeadRowByCin(leads, cin) {
    const id = String(cin || "")
        .trim()
        .toUpperCase();
    if (!id) {
        return null;
    }
    const list = Array.isArray(leads) ? leads : [];
    return list.find((r) => String(r?.cin || "").trim().toUpperCase() === id) || null;
}
